import { getOrderNumberSuffix } from '@/utils/markerUtils';

const STATUS_STYLES = {
  new: ['#fef3c7', '#fcd34d', '#92400e', 'New'],
  accepted: ['#dbeafe', '#93c5fd', '#1e40af', 'Accepted'],
  designed: ['#f3e8ff', '#c4b5fd', '#6b21a8', 'Designed'],
  on_route: ['#dcfce7', '#86efac', '#166534', 'On Route'],
  delivered: ['#f5f5f4', '#d6d3d1', '#57534e', 'Delivered'],
};

// Styles, tags and attributes are application constants. Order values enter
// only text nodes or callback closures, never HTML, selectors or attributes.
function element(tag, style, text) {
  const node = document.createElement(tag);
  node.style.cssText = style;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createMarkerPopup({
  order, stopInfo, hideActions, selectedRun,
  onAddToRun, onRemoveFromRun, onRemoveFromCommittedRun, onEditFlag, onClose,
  isCurrent = () => true,
}) {
  const content = element('div', 'min-width: 200px; padding: 8px;');
  const listeners = [];
  let active = true;
  const heading = element('div', 'font-weight: 600; font-size: 14px; margin-bottom: 4px;');
  if (order.internal_order_number) {
    heading.append(element('span', 'color: #3b82f6;', `#${getOrderNumberSuffix(order.internal_order_number)}`), ' ');
  }
  heading.append(document.createTextNode(order.recipient_name || 'Unknown'));
  content.append(heading);

  const address = element('div', 'font-size: 12px; color: #666; margin-bottom: 6px; line-height: 1.4;');
  const cityStateZip = `${order.recipient_city || ''}, ${order.recipient_state || ''} ${order.recipient_zip_code || ''}`.trim();
  for (const line of [order.recipient_street_address, order.recipient_street_address_2, cityStateZip]) {
    if (line) address.append(element('div', '', line));
  }
  content.append(address);

  const status = order.status || 'designed';
  const [background, border, color, label] = Object.prototype.hasOwnProperty.call(STATUS_STYLES, status)
    ? STATUS_STYLES[status] : STATUS_STYLES.designed;
  const badgeRow = element('div', 'margin-bottom: 8px;');
  badgeRow.append(element('span', `display: inline-block; padding: 2px 8px; background: ${background}; border: 1px solid ${border}; border-radius: 12px; font-size: 11px; font-weight: 500; color: ${color};`, label));
  content.append(badgeRow);

  const note = (order.dispatch_note || '').trim();
  if (onEditFlag && note) {
    content.append(element('div', 'margin: 8px 0; padding: 6px 8px; background: #fef2f2; border-left: 3px solid #dc2626; border-radius: 4px; font-size: 12px; color: #7f1d1d; white-space: pre-wrap;', note));
  }

  function button(testId, text, style, action, disabled = false) {
    const node = element('button', style, text);
    node.type = 'button';
    node.dataset.testid = testId;
    node.disabled = disabled;
    const click = () => {
      if (!active || node.disabled || !isCurrent()) return;
      action();
      onClose();
    };
    node.addEventListener('click', click);
    listeners.push(() => node.removeEventListener('click', click));
    return node;
  }

  if (!hideActions) {
    const actions = element('div', 'display: flex; gap: 8px; margin-top: 8px;');
    if (stopInfo !== undefined) {
      actions.append(button('map-popup-remove', 'Remove from Run', 'flex: 1; padding: 6px 12px; background: #fee2e2; color: #dc2626; border: none; border-radius: 6px; cursor: pointer; font-size: 12px;', () => {
        if (selectedRun?.status !== 'draft') onRemoveFromCommittedRun(order.id, order.recipient_name);
        else onRemoveFromRun(order.id);
      }));
    } else {
      actions.append(button('map-popup-add', 'Add to Run', 'flex: 1; padding: 6px 12px; background: #dcfce7; color: #16a34a; border: none; border-radius: 6px; cursor: pointer; font-size: 12px;', () => onAddToRun(order.id), !selectedRun));
    }
    content.append(actions);
  }
  if (onEditFlag) {
    content.append(button('map-popup-flag', `⚑ ${note ? 'Edit note' : 'Flag order'}`, `width: 100%; padding: 6px 12px; background: ${note ? '#fef2f2' : '#f5f5f4'}; color: ${note ? '#dc2626' : '#57534e'}; border: 1px solid ${note ? '#fecaca' : '#e7e5e4'}; border-radius: 6px; cursor: pointer; font-size: 12px; margin-top: 8px;`, () => onEditFlag(order.id)));
  }

  return {
    content,
    dispose() {
      active = false;
      listeners.forEach(remove => remove());
    },
  };
}
